import { useState } from "react";
import type { MediaItem } from "@/content/site-content";

/**
 * Tekstowa lista materiałów medialnych: nazwa źródła, tytuł i link.
 * Domyślnie widoczne pierwsze pozycje, reszta po kliknięciu „Pokaż więcej”.
 */

function Chevron({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`h-4 w-4 transition-transform duration-300 ${up ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Row({ item, linkLabel }: { item: MediaItem; linkLabel: string }) {
  return (
    <li className="border-t border-steel py-5 first:border-t-0 first:pt-0">
      <p className="text-xs uppercase tracking-[0.14em] text-ink-soft">{item.name}</p>
      <p className="mt-2 font-serif text-[1.05rem] leading-snug text-ink">
        {`\u201E${item.topic}\u201D`}
      </p>
      {item.href ? (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-2 inline-block text-sm font-medium text-coral-text underline underline-offset-4 hover:no-underline"
        >
          {linkLabel} →<span className="sr-only"> (otwiera się w nowej karcie)</span>
        </a>
      ) : null}
    </li>
  );
}

export function MediaLinks({
  items,
  initial = 3,
  linkLabel = "Zobacz rozmowę",
  moreLabel = "Pokaż więcej",
  lessLabel = "Pokaż mniej",
}: {
  items: MediaItem[];
  initial?: number;
  linkLabel?: string;
  moreLabel?: string;
  lessLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const head = items.slice(0, initial);
  const rest = items.slice(initial);

  return (
    <div>
      <ul>
        {head.map((item) => (
          <Row key={item.href ?? item.topic} item={item} linkLabel={linkLabel} />
        ))}
      </ul>
      {rest.length > 0 ? (
        <>
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
            style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <ul aria-hidden={!expanded} inert={!expanded}>
                {rest.map((item) => (
                  <Row key={item.href ?? item.topic} item={item} linkLabel={linkLabel} />
                ))}
              </ul>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-coral-text underline underline-offset-4 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-coral"
          >
            {expanded ? lessLabel : moreLabel}
            <Chevron up={expanded} />
          </button>
        </>
      ) : null}
    </div>
  );
}
